import { useParams, Navigate, Link } from "react-router-dom";
import { Clock, ArrowRight, ArrowUpRight } from "lucide-react";
import Seo, { breadcrumbSchema, articleSchema } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Btn, Overline } from "@/components/Btn";
import { Breadcrumb } from "@/components/Breadcrumb";
import { CtaBand } from "@/components/CtaBand";
import { getArticle } from "@/data/conseils";
import { getService } from "@/data/services";

export default function ConseilDetail() {
  const { slug } = useParams();
  const a = getArticle(slug);
  if (!a) return <Navigate to="/404" replace />;

  const path = `/conseils/${a.slug}/`;
  const crumbs = [
    { label: "Accueil", to: "/" },
    { label: "Conseils", to: "/conseils/" },
    { label: a.title, to: path },
  ];

  return (
    <>
      <Seo title={`${a.title} | Conseils – Ronalp Enseigne`} description={a.metaDescription} path={path} image={a.image} type="article" jsonLd={[articleSchema(a, path), breadcrumbSchema(crumbs)]} />

      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
        <Breadcrumb items={crumbs} />
        <Reveal className="mt-8">
          <Overline>Conseils</Overline>
          <h1 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.02]">{a.title}</h1>
          <p className="mt-4 flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-chrome-dark">
            <Clock className="h-3.5 w-3.5" /> {a.readingTime} de lecture
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <img src={a.image} alt={a.title} className="w-full h-[300px] sm:h-[400px] object-cover rounded-2xl border border-white/10" />
        </Reveal>
      </section>

      <article className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        <div className="space-y-6">
          {a.blocks.map((b, i) =>
            b.type === "h2" ? (
              <h2 key={i} className="text-2xl font-bold text-white pt-4">{b.text}</h2>
            ) : (
              <p key={i} className="text-base sm:text-lg text-slate-300 leading-relaxed">{b.text}</p>
            ),
          )}
        </div>

        <div className="mt-12 rounded-2xl border border-signature/30 bg-gradient-to-b from-ink-800 to-ink-900 p-8 glow-red">
          <h3 className="text-xl font-bold text-white">Un projet en tête ?</h3>
          <p className="mt-2 text-slate-300">Parlons-en. Étude et devis gratuits, de la conception à la pose.</p>
          <Btn to="/contact/" className="mt-5">Demander un devis <ArrowRight className="h-4 w-4" /></Btn>
        </div>

        {a.related && (
          <div className="mt-10">
            <p className="text-xs font-mono uppercase tracking-wider text-signature mb-4">Services liés</p>
            <div className="flex flex-wrap gap-3">
              {a.related.map((sl) => {
                const svc = getService(sl);
                if (!svc) return null;
                return (
                  <Link key={sl} to={`/${svc.slug}/`} className="group inline-flex items-center gap-2 rounded-lg border border-white/10 bg-ink-800 px-4 py-2.5 text-sm text-slate-300 hover:border-signature/40 hover:text-white transition-all">
                    {svc.nav} <ArrowUpRight className="h-3.5 w-3.5 text-chrome-dark group-hover:text-signature" />
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </article>

      <CtaBand />
    </>
  );
}
